import { PriceTypes } from "legacyComponents/LineItemContainer.types";
import moment from "moment";

import { InvoiceFormat } from "@btnet/services-btnet/handlers/models/invoiceFormat";
import type { InvoiceLineItemMetadata } from "@btnet/services-btnet/handlers/models/invoiceLineItemMetadata";

import { type BTSelectItem } from "types/apiResponse/apiResponse";
import { JobContractType } from "types/enum";

import { normalizeCustomFieldFormValueForDisplay } from "utilities/customFields/customFields";
import { type ICostCodeType } from "utilities/lineItem/lineItemHelper";
import { divide, round, sum } from "utilities/math/math";
import {
    getTaxRateBreakdownForPriceBreakdown,
    isTaxGroupIdValid,
} from "utilities/tax/tax.utilities";

import { CustomFieldPrintItem } from "commonComponents/entity/customField/CustomFieldContainer/CustomFieldContainer.types";
import { type TaxGroupServiceListItemExtraData } from "commonComponents/financial/TaxRateSelect/TaxRateSelect.api.types";

import {
    type IOwnerInvoiceFormValues,
    type OwnerInvoiceEntity,
} from "entity/ownerInvoice/OwnerInvoice/OwnerInvoice.api.types";
import { type IOwnerInvoiceLineItem } from "entity/ownerInvoice/OwnerInvoiceLineItemContainer/OwnerInvoiceLineItemContainer.types";
import {
    type IInvoiceLineItemPrintData,
    InvoiceLineItemPrintEntity,
    OnlinePaymentPrintEntity,
    type OwnerInvoicePrintEntity,
} from "entity/ownerInvoice/OwnerInvoicePrint/OwnerInvoicePrint.api.types";
import { TaxMethod } from "entity/tax/common/tax.types";
import { type ISubtotalBreakdown } from "entity/tax/common/TaxRateBreakdown/TaxRateBreakdown.api.types";

export function buildCostCodeDictFromLineItems(
    items: IOwnerInvoiceLineItem[],
    serverDict: Map<number, ICostCodeType>,
): Map<number, ICostCodeType> {
    const merged = new Map(serverDict);
    for (const li of items) {
        if (li.costCodeId != null && li.costCodeTitle) {
            const existing = merged.get(li.costCodeId);
            merged.set(li.costCodeId, {
                ...existing,
                id: li.costCodeId,
                name: li.costCodeTitle,
            } as ICostCodeType);
        }
    }
    return merged;
}

/**
 * Stored materials billed this period, expressed on the same tax basis as the line's ownerPrice.
 * storedMaterials is persisted tax-inclusive, so under the exclusive method the period amount is
 * reduced by storedMaterialsTax to get the pre-tax value.
 */
export function getStoredMaterialsThisPeriod(
    lineItem: { storedMaterials?: number; invoiceLineItemMetadata?: InvoiceLineItemMetadata },
    taxMethod: TaxMethod | null | undefined,
): number {
    const storedMaterialsThisPeriod =
        (lineItem.storedMaterials ?? 0) -
        (lineItem.invoiceLineItemMetadata?.previousStoredMaterials ?? 0);
    if (taxMethod === TaxMethod.Inclusive) {
        return storedMaterialsThisPeriod;
    }
    return storedMaterialsThisPeriod - (lineItem.invoiceLineItemMetadata?.storedMaterialsTax ?? 0);
}

/**
 * The builder cost and markup the live line items add up to — the Client preview tab's counterpart
 * to the print response's breakdown, under the same rules as the server's
 * `InvoiceManager.GetSubtotalBreakdown`.
 */
export function getSubtotalBreakdown(
    ownerInvoice: OwnerInvoiceEntity,
    lineItems: IOwnerInvoiceLineItem[],
    isDetailMode: boolean,
): ISubtotalBreakdown | undefined {
    if (
        ownerInvoice.jobContractType !== JobContractType.OpenBook ||
        ownerInvoice.invoiceFormat !== InvoiceFormat.Invoice ||
        !isDetailMode
    ) {
        return undefined;
    }
    const markupAmount = sum(lineItems.map((li) => li.markupAmount ?? 0)).toNumber();
    if (markupAmount === 0) {
        return undefined;
    }
    const markupPercentages = [...new Set(lineItems.map((li) => li.markupPercent ?? 0))];
    return {
        builderCost: sum(lineItems.map((li) => li.builderCost ?? 0)).toNumber(),
        markupAmount,
        markupPercentage:
            markupPercentages.length === 1 && markupPercentages[0] !== 0
                ? markupPercentages[0]
                : null,
    };
}

/**
 * The tax rate breakdown the builder preview renders. Exported so the Customize drawer decides
 * whether there are agency rows to hide from the same gate and inputs the preview uses.
 */
export function buildLiveTaxRateBreakdown(
    ownerInvoice: OwnerInvoiceEntity,
    values: IOwnerInvoiceFormValues,
    taxGroups?: BTSelectItem<TaxGroupServiceListItemExtraData>[],
) {
    if (!isTaxGroupIdValid(ownerInvoice.taxGroupId)) {
        return null;
    }
    // Fold each line's stored materials for the period into its taxable amount so the per-rate
    // breakdown rows and total tax include the stored materials tax
    const lineItemsWithStoredMaterials = values.ownerInvoiceLineItems.map((li) => {
        const storedMaterialsThisPeriod = getStoredMaterialsThisPeriod(li, values.taxMethod);
        return storedMaterialsThisPeriod === 0
            ? li
            : { ...li, ownerPrice: (li.ownerPrice ?? 0) + storedMaterialsThisPeriod };
    });
    return getTaxRateBreakdownForPriceBreakdown(
        lineItemsWithStoredMaterials,
        false,
        true,
        taxGroups,
        values.taxMethod,
    );
}

export function buildPreviewData(
    ownerInvoice: OwnerInvoiceEntity,
    values: IOwnerInvoiceFormValues,
    taxGroups?: BTSelectItem<TaxGroupServiceListItemExtraData>[],
    costCodeMap?: Map<number, ICostCodeType>,
): OwnerInvoicePrintEntity | undefined {
    if (!ownerInvoice.printHeader) {
        return undefined;
    }

    const costCodeDict = buildCostCodeDictFromLineItems(
        values.ownerInvoiceLineItems,
        costCodeMap ?? new Map<number, ICostCodeType>(),
    );
    const lineItems = mapOwnerInvoiceLineItemsToPrintEntities(
        values.ownerInvoiceLineItems,
        costCodeDict,
    );
    const invoiceDate = values.invoiceDate ? moment(values.invoiceDate) : moment();
    const customInvoiceId = values.customInvoiceId ?? "";
    // Same prefix the Details tab shows as the id field's addon; the server prepends it on the printout.
    const customerInvoiceId =
        ownerInvoice.invoiceJobPrefixes && customInvoiceId
            ? `${ownerInvoice.invoiceJobPrefixes}-${customInvoiceId}`
            : customInvoiceId;
    const amount = values.amount ?? 0;
    const liveTaxBreakdown = buildLiveTaxRateBreakdown(ownerInvoice, values, taxGroups);
    const amountPaid = values.amountPaid ?? 0;

    const invoicePayments = (ownerInvoice.invoicePayments ?? []).map(
        (p) =>
            new OnlinePaymentPrintEntity({
                paymentDate: p.paymentDate,
                paymentAmount: p.paymentAmount,
                paymentMethod: p.paymentMethod,
                paymentStatus: p.paymentStatus,
                refundReason: p.refundReason,
                feeAmount: p.feeAmount ?? 0,
            }),
    );

    const customFields = ownerInvoice.customFields
        .filter((meta) => meta.onOwnerPortal)
        .map((meta) => {
            const formValue = values.customFields.find((v) => v.id === meta.id);
            const printField = ownerInvoice.customFieldsPrint.find((p) => p.label === meta.title);

            const normalized = normalizeCustomFieldFormValueForDisplay(
                formValue?.value ?? null,
                meta.typeId,
            );

            let value: string | number | boolean;
            if (
                typeof normalized === "string" ||
                typeof normalized === "number" ||
                typeof normalized === "boolean"
            ) {
                value = normalized;
            } else if (normalized instanceof Date) {
                value = normalized.toISOString();
            } else {
                return (
                    printField ??
                    new CustomFieldPrintItem({
                        type: meta.typeId,
                        label: meta.title,
                        value: "",
                    })
                );
            }

            return new CustomFieldPrintItem({
                type: printField?.type ?? meta.typeId,
                label: meta.title,
                value,
            });
        });

    // Mirrors the server's IsDetailMode: priceType is hydrated from response.UseLineItems, which
    // GetLineItemFormatForContainer derives from the invoice's IsDetailMode. Reading it live keeps
    // an unsaved invoice correct, where the persisted flag would be stale.
    const isDetailMode = values.priceType === PriceTypes.LineItems;

    return {
        // The server returns this unconditionally for BTLoginTypes.BUILDER
        // (InvoiceUtility.CanViewOwnerInvoiceLineItems) and this builds a builder-side preview.
        hasPermissionToViewLineItems: true,
        hasViewableLineItems: isDetailMode,
        canShowPaymentFromPrintoutInformation: ownerInvoice.canShowPaymentFromPrintoutInformation,
        LineItems: lineItems,
        showLineItems: values.showLineItemsToOwner,
        ownerInvoiceLineItemColumnPreferences: values.columnPreferences ?? [],
        hasCustomFields: ownerInvoice.hasCustomFields || customFields.length > 0,
        customFields: customFields,
        customerInvoiceId,
        // No client-side equivalent: the server derives this display string through
        // BTSqlTransforms.CustomerInvoices.GetPaymentStatus. Nothing in the preview reads it.
        Status: "",
        // `?? null` is load-bearing on every date below: moment(undefined) is the current time,
        // moment(null) is an invalid moment. The server sends null for an unset date, so an invalid
        // moment is what the `.isValid()` guards downstream expect in order to hide the row.
        DueDate: moment(values.unifiedDeadlineRequest?.dueDate ?? null),
        releasedDate: moment(ownerInvoice.releasedDate ?? null),
        invoiceDate,
        paymentSum: ownerInvoice.amountPaid ?? amountPaid,
        Description: values.description ?? "",
        invoicePayments,
        invoice: {
            id: ownerInvoice.id,
            title: values.title ?? "",
            amount,
            amountPaid,
            // Faithful, not a stub: CustomerInvoiceForPrintResponse never sets PaidDate either.
            paidDate: "",
            description: values.description ?? "",
            closingText: values.closingText ?? "",
            builderID: ownerInvoice.builderId,
            jobID: ownerInvoice.jobId,
            isDetailMode,
            totalPrice: amount,
            taxMethod: values.taxMethod,
            taxRateBreakdown: liveTaxBreakdown,
        },
        // Server: lastPaymentDateCellVisible / txtPaidDate, both driven off a non-null PaidDate.
        showPaymentDateCell: ownerInvoice.paidDate != null,
        PaidDate: moment(ownerInvoice.paidDate ?? null),
        allowCustomFields: values.showCustomFields ?? false,
        jobsite: {
            jobAddress: ownerInvoice.previewJobsite?.jobAddress ?? "",
            jobName: ownerInvoice.jobName ?? "",
            ownerAddress: ownerInvoice.previewJobsite?.ownerAddress ?? "",
            ownerContact: ownerInvoice.previewJobsite?.ownerContact ?? "",
        },
        builderId: ownerInvoice.builderId,
        showMerchantPayments: ownerInvoice.showMerchantPayments,
        printHeader: ownerInvoice.printHeader,
        showPaymentFromPrintoutInformation: ownerInvoice.showPaymentFromPrintoutInformation,
        qrCodeUrl: ownerInvoice.qrCodeUrl ?? null,
        paymentCode: ownerInvoice.paymentCode ?? null,
        taxMethod: values.taxMethod,
        showFees: ownerInvoice.showFees,
        groupLineItems: values.groupLineItemsByCostCode,
        lineItemGroupStrategy: values.lineItemGroupStrategy,
        invoiceFormat: ownerInvoice.invoiceFormat,
        costCodeDict,
        originalContractSum: ownerInvoice.originalContractSum,
        netChangeOrderTotal: ownerInvoice.netChangeOrderTotal,
        subtotalBreakdown: getSubtotalBreakdown(
            ownerInvoice,
            values.ownerInvoiceLineItems,
            isDetailMode,
        ),
    } as OwnerInvoicePrintEntity;
}

export function mapOwnerInvoiceLineItemsToPrintEntities(
    items: IOwnerInvoiceLineItem[],
    costCodeMap?: Map<number, ICostCodeType>,
): InvoiceLineItemPrintEntity[] {
    return items.map(
        (x, idx) =>
            new InvoiceLineItemPrintEntity(
                {
                    invoiceLineItemMetadata: x.invoiceLineItemMetadata!,
                    billLineItemId: x.relatedBillLineItemId ?? null,
                    relatedAccountingCostId: x.relatedAccountingCostId ?? null,
                    relatedStackLinks: x.relatedStackLinks,
                    title: x.itemTitle,
                    id: x.id,
                    builderId: 0,
                    entityId: 0,
                    costCodeId: x.costCodeId ?? 0,
                    // Builder rows arrive without a title, so fall back to the cost code map.
                    costCodeTitle:
                        x.costCodeTitle ??
                        (x.costCodeId != null ? costCodeMap?.get(x.costCodeId)?.name : null) ??
                        "",
                    description: x.description,
                    quantity: x.quantity,
                    unitType: x.unit,
                    unitCost: x.unitCost,
                    // Derived, not read off the row: the persisted unitPrice is only populated by
                    // the server on save, so an unsaved invoice would render the column blank.
                    // Mirrors LineItemsEngine.GetLineItem so the builder preview and the owner-facing
                    // view agree. Summing unitCost + markupPerUnit instead doubles every flat-fee
                    // invoice: CreateDefaultMarkupLineItem stamps the full amount into unitCost,
                    // ownerPrice, markupPrice AND markupPerUnit on its one synthetic row.
                    unitPrice:
                        x.quantity && x.ownerPrice != null
                            ? round(divide(x.ownerPrice, x.quantity), 4)
                            : 0,
                    ownerPrice: x.ownerPrice,
                    costTypes: x.costTypes,
                    markedAs: x.markedAs,
                    // Formik holds the NoTaxId/ImportOptionId sentinels where the server print DTO sends null.
                    taxGroupId: isTaxGroupIdValid(x.taxGroupId) ? x.taxGroupId : undefined,
                    totalWithTax: x.totalWithTax,
                    markupPercentage: x.markupPercent,
                    markupAmount: x.markupAmount,
                    markupColumn: x.markupType,
                    markupPerUnit: x.markupPerUnit,
                    builderCost: x.builderCost,
                    storedMaterials: x.storedMaterials,
                    retainageWithheld: x.retainageWithheld,
                    isStackedLineItem: x.isStackedLineItem,
                    relatedItems: x.relatedItems,
                    files: x.files,
                } as IInvoiceLineItemPrintData,
                idx,
            ),
    );
}
